
import { Component, Vue } from "vue-property-decorator";
import { documentService } from "@/services/document.service";
import Loader from "@/components/common/Loader.vue";
import ActionTile from "@/app/components/common/ActionTile.vue";
import UploadDocumentTile from "@/components/documents/upload/UploadDocumentTile.vue";
import AuthenticationInterceptor from "@/components/common/AuthenticationInterceptor.vue";
import AcceptPoliciesInterceptor from "@/components/common/AcceptPoliciesInterceptor.vue";
import DocumentsTable from "@/components/common/documents/DocumentsTable.vue";
import { DocumentDto } from "@/contracts/document/DocumentsDto";
import DocumentsFilters from "@/components/common/documents/DocumentsFilters.vue";
import PageConfig from "@/contracts/generic/PageConfig";
import SortConfig from "@/contracts/generic/SortConfig";
import DocumentFilterConfig from "@/contracts/document/DocumentFilterConfig";
import { documentMapper } from "@/mappers/document.mapper";
import { i18n } from "@/i18n";
import CreateDocumentDto from "@/contracts/document/CreateDocumentDto";
import { roleBasedActionsService } from "@/services/role-based-actions.service";
import { TableColumns } from "@/constants/TableColumns";
import PageHeader from "@/components/layout/PageHeader.vue";
import { AxiosPromise } from "axios";

@Component({
  components: {
    DocumentsFilters,
    DocumentsTable,
    AcceptPoliciesInterceptor,
    AuthenticationInterceptor,
    UploadDocumentTile,
    ActionTile,
    Loader,
    PageHeader,
  },
})
export default class MyDocumentsView extends Vue {
  initialLoading = false;
  loading = false;

  pageConfig: PageConfig = {
    page: 1,
    pageSize: 10,
  };

  sortConfig: SortConfig = {
    sortBy: TableColumns.DATE,
    sortDesc: true,
  };

  filterConfig: DocumentFilterConfig = {
    categories: [],
    types: [],
    activities: [],
    uploadedBy: [],
    organizations: [],
  };

  amountOfDocuments = 0;
  initialFilterConfig = JSON.stringify(this.filterConfig);
  documents: DocumentDto[] = [];

  get userIsAuthorizedToCreateDocument(): boolean {
    return roleBasedActionsService.userIsAuthorizedToCreateDocument();
  }

  get userIsAuthorizedToDeleteDocument(): boolean {
    return roleBasedActionsService.userIsAuthorizedToDeleteDocument();
  }

  get userIsAuthorizedToFilterDocumentsByOrganization(): boolean {
    return roleBasedActionsService.userIsAuthorizedToFilterDocumentsByOrganization();
  }

  mounted(): void {
    this.loadData();
  }

  loadData(): void {
    this.fetchDocuments(true);
  }

  fetchDocuments(initialFetch = false): void {
    if (initialFetch) {
      this.initialLoading = true;
    }

    this.loading = true;

    documentService
      .getDocuments(this.pageConfig, this.sortConfig, this.filterConfig)
      .then((data) => {
        if (data) {
          if (initialFetch) {
            this.amountOfDocuments = data.rowCount;
          }

          if (data.results) {
            this.documents = data.results;
          }
        }
      })
      .finally(() => {
        if (initialFetch) {
          this.initialLoading = false;
        }

        this.loading = false;
      });
  }

  pageConfigUpdated(pageConfig: PageConfig): void {
    this.pageConfig = pageConfig;
    this.fetchDocuments();
  }

  sortConfigUpdated(sortConfig: SortConfig): void {
    this.sortConfig = sortConfig;
    this.fetchDocuments();
  }

  filterConfigUpdated(filterConfig: DocumentFilterConfig): void {
    this.filterConfig = filterConfig;
    this.fetchDocuments();
  }

  documentAdded(document: CreateDocumentDto | null): void {
    if (document) {
      this.loading = true;
      let files = document.files;
      document.files = null;
      let promises: AxiosPromise<void>[] = [];

      files?.forEach((value) => {
        document.files = [value];
        let formData = documentMapper.mapCreateDocumentDtoToFormData(document);

        if (value instanceof File) {
          promises.push(documentService.addDocument(formData));
        }
      });

      Promise.all(promises)
        .then(() => {
          this.filterConfig = JSON.parse(this.initialFilterConfig);
          this.fetchDocuments(true);
          Vue.$toast.success(i18n.t("my-documents.document-added"));
        })
        .catch(() => {
          Vue.$toast.error(i18n.t("my-documents.document-add-failed"));
        })
        .finally(() => (this.loading = false));
    }
  }
}
